import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Typography, Spin, Flex, Table, message } from 'antd';
import { EyeOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CommitmentDetail, getCommitmentById } from '@/api/commitment';
import { CommitmentStatus } from '@/components/CommitmentStatus';
import AssetSymbol from '@/components/AssetView/AssetSymbol';
import { isSupportedChainId, SupportedChainId, SupportedChains } from '@packet/constants/AppSupportChains';
import AssetAmount from '@/components/AssetView/AssetAmount';
import { timeFmt } from '@/utils/timeFmt';
import { truncateAddress } from '@/utils/address';
import UtxoTreeFlow from './UtxoTreeFlow';
import {
  UtxoDetailsContainer,
  MainContent,
  HeaderSection,
  PageTitle,
  UtxoIdSection,
  UtxoIdText,
  CopyBtn,
  CopyBtnSmall,
  OverviewSection,
  SectionTitle,
  InfoGrid,
  InfoRow,
  InfoLabel,
  InfoValue,
  TabsSection,
  TabButtons,
  TabBtn,
  TxHashContent,
  TxHashGrid,
  TxHashSection,
  TxHashHeader,
  TxHashLabel,
  TxHashValue,
  HashText,
  FamilyTables,
  FamilyTableSection,
  OperateButtons,
  OperateBtn,
  StyledTable,
  ResponsiveWrapper,
} from './UtxoDetailsStyle';
import CommitmentFlow from '@/components/CommitmentFlow';
import AddressItem from '@/components/AddressItem';
import { AddressType } from '@/types/address';
import { FreezeButton } from './UtxoDetails/FreezeButton';
import { useHasPermission } from '@/hooks/usePermissions';

const tabs = ['hash', 'tree'] as const;

const UtxoDetails: React.FC = () => {
  const { id, networkId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // 从 URL hash 中获取当前 activeTab，如果没有则默认为 'hash'
  const getActiveTabFromHash = () => {
    const hash = location.hash.replace('#', '') as (typeof tabs)[number];
    return tabs.includes(hash) ? hash : 'hash';
  };
  const [activeTab, setActiveTab] = useState(getActiveTabFromHash);
  const [isPollingSpent, setIsPollingSpent] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 监听 location 变化，同步 activeTab
  useEffect(() => {
    setActiveTab(getActiveTabFromHash());
  }, [location.hash]);

  // 清理轮询定时器
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  // 切换 tab 时更新 URL hash
  const handleTabChange = (tab: (typeof tabs)[number]) => {
    setActiveTab(tab);
    navigate({ ...location, hash: `#${tab}` }, { replace: true });
  };

  const { chainId, chain } = useMemo(() => {
    const id = parseInt(networkId || '0');
    if (isSupportedChainId(id)) {
      const chain = SupportedChains.find((chain) => chain.id === id)!;
      return { chainId: id, chain };
    }
    throw new Error('Invalid chainId');
  }, [networkId]);

  const query = useQuery({
    queryKey: ['utxo', id, chainId],
    queryFn: async () => {
      if (!id) return Promise.resolve(null);
      console.log('Fetching UTXO details for ID:', id);
      const res = await getCommitmentById([id]);
      console.log('res', res);
      if (!res) return Promise.resolve(null);
      return res;
    },
  });
  const utxo = query.data?.[0];
  const canFreeze = useHasPermission('freeze', chainId, utxo?.asset);

  const txs = useMemo(() => {
    const created = utxo?.from?.[0];
    const consumed = utxo?.to?.[0];
    return {
      created: created ? `${created.txhash}-${created.tx_index}` : undefined,
      consumed: consumed ? `${consumed.txhash}-${consumed.tx_index}` : undefined,
    };
  }, [utxo?.from, utxo?.to]);

  const spent_time = useMemo(() => {
    const spent = utxo?.to?.[0];
    if (!spent) return null;
    return spent.created_at;
  }, [utxo]);

  // 轮询检查资产是否已消费
  const pollForSpentStatus = useCallback(async () => {
    if (!id || !utxo) return;

    try {
      console.log('Polling for spent status...');
      const result = await query.refetch();
      const updatedUtxo = result.data?.[0];

      if (updatedUtxo?.is_spent) {
        console.log('Asset is now spent, stopping polling');
        setIsPollingSpent(false);
        return;
      }

      // 如果还没有被消费，继续轮询
      pollingTimeoutRef.current = setTimeout(() => {
        pollForSpentStatus();
      }, 2000); // 每2秒轮询一次
    } catch (error) {
      console.error('Error polling spent status:', error);
      setIsPollingSpent(false);
    }
  }, [id, utxo, query]);

  // 增强的onSuccess处理函数
  const handleOnSuccess = useCallback(async () => {
    // 清理之前的轮询
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }

    setIsPollingSpent(true);
    // 开始轮询检查is_spent状态
    pollForSpentStatus();
  }, [query, pollForSpentStatus]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  if (query.isLoading) {
    return (
      <Flex align="center" justify="center" style={{ height: '30vh' }}>
        <Spin />
      </Flex>
    );
  }

  if (!utxo) {
    return (
      <Flex align="center" justify="center" style={{ height: '30vh' }}>
        <Typography.Text>No UTXO found</Typography.Text>
      </Flex>
    );
  }

  return (
    <ResponsiveWrapper>
      <UtxoDetailsContainer>
        {/* Main Content */}
        <MainContent>
          <HeaderSection>
            <PageTitle level={3}>UTXO Details</PageTitle>
            {canFreeze && <FreezeButton chainId={chainId} utxo={utxo} onSuccess={handleOnSuccess} />}
          </HeaderSection>

          {/* UTXO ID Display */}
          <UtxoIdSection>
            <UtxoIdText>{utxo.commitment}</UtxoIdText>
            <CopyBtn
              type="text"
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(utxo.commitment || '');
              }}
            />
          </UtxoIdSection>

          {/* Overview Section */}
          <OverviewSection>
            <SectionTitle level={4}>Overview</SectionTitle>

            <InfoGrid>
              <InfoRow>
                <InfoLabel>Creation Time</InfoLabel>
                <InfoValue>{timeFmt(utxo.created_at)}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>State</InfoLabel>
                <InfoValue>
                  <Flex align="center" gap={8}>
                    <CommitmentStatus is_frozen={utxo.is_frozen} is_spent={utxo.is_spent} />
                    {isPollingSpent && (
                      <Flex align="center" gap={4}>
                        <Spin size="small" />
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                          Checking status updates...
                        </Typography.Text>
                      </Flex>
                    )}
                  </Flex>
                </InfoValue>
              </InfoRow>

              {spent_time && (
                <InfoRow>
                  <InfoLabel>Spent Time</InfoLabel>
                  <InfoValue>{timeFmt(spent_time)}</InfoValue>
                </InfoRow>
              )}

              <InfoRow>
                <InfoLabel>Network</InfoLabel>
                <InfoValue>{chain.name}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Owner</InfoLabel>
                <InfoValue>
                  <AddressItem chainId={chainId} key={utxo.user_pk} address={utxo.user_pk} type={AddressType.APP} />
                </InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Token</InfoLabel>
                <InfoValue>
                  <AssetSymbol assetId={utxo.asset} chainId={chainId} />
                </InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Amount</InfoLabel>
                <InfoValue>
                  <AssetAmount amount={utxo.amount} assetId={utxo.asset} chainId={chainId} /> <AssetSymbol assetId={utxo.asset} chainId={chainId} />
                </InfoValue>
              </InfoRow>
            </InfoGrid>
          </OverviewSection>

          {/* Tabs Section */}
          <TabsSection>
            <TabButtons>
              <TabBtn $active={activeTab === 'hash'} onClick={() => handleTabChange('hash')}>
                Tx Hash
              </TabBtn>
              <TabBtn $active={activeTab === 'tree'} onClick={() => handleTabChange('tree')}>
                UTXO Tree
              </TabBtn>
            </TabButtons>

            {activeTab === 'hash' && (
              <TxHashContent>
                <TxHashGrid>
                  <TxHashSection>
                    <TxHashHeader>
                      <TxHashLabel>Transaction Hash From</TxHashLabel>
                    </TxHashHeader>
                    <TxHashValue to={`/transaction/${chainId}/${txs.created}`}>
                      <HashText>{txs.created}</HashText>
                      <CopyBtnSmall
                        className="copy-btn"
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(txs.created || '');
                        }}
                      />
                    </TxHashValue>
                  </TxHashSection>

                  {txs.consumed && (
                    <TxHashSection>
                      <TxHashHeader>
                        <TxHashLabel>Transaction Hash To</TxHashLabel>
                      </TxHashHeader>
                      <TxHashValue to={`/transaction/${chainId}/${txs.consumed}`}>
                        <HashText>{txs.consumed}</HashText>
                        <CopyBtnSmall
                          type="text"
                          icon={<CopyOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(txs.consumed!);
                          }}
                        />
                      </TxHashValue>
                    </TxHashSection>
                  )}
                </TxHashGrid>

                <CommitmentFamily commitment={utxo} chainId={chainId} activeTab={activeTab} />
              </TxHashContent>
            )}
            {activeTab === 'tree' && <CommitmentFlow commitment={utxo} chainId={chainId} />}
          </TabsSection>
        </MainContent>
      </UtxoDetailsContainer>
    </ResponsiveWrapper>
  );
};

export default UtxoDetails;

/**
 * 父/子/兄弟 commitments 数据加载和展示
 */
const CommitmentFamily: React.FC<{
  commitment: CommitmentDetail;
  chainId: SupportedChainId;
  activeTab: string;
}> = ({ commitment, chainId, activeTab }) => {
  const navigate = useNavigate();
  const familyIds = useMemo(() => {
    const siblings1 = commitment.from?.[0]?.outputs || [];
    const siblings2 = commitment.to?.[0]?.inputs || [];
    const parents = commitment.from?.[0]?.inputs || [];
    const children = commitment.to?.[0]?.outputs || [];
    const all = [
      ...parents.map((id) => ({ id, key: `parent-${id}`, type: 'parent' })),
      ...siblings1.map((id) => ({
        id,
        key: `sibling1-${id}`,
        type: 'sibling of created',
      })),
      ...siblings2.map((id) => ({
        id,
        key: `sibling2-${id}`,
        type: 'sibling of consumed',
      })),
      ...children.map((id) => ({ id, key: `child-${id}`, type: 'child' })),
    ].filter((item) => item.id !== commitment.commitment);
    return { siblings1, siblings2, parents, children, all };
  }, [commitment]);

  const query = useQuery({
    queryKey: ['commitments', familyIds, chainId],
    queryFn: async () => {
      const ids = new Set(familyIds.all.map((item) => item.id));
      if (ids.size === 0) return {};
      const reqs = await getCommitmentById(Array.from(ids));
      const map: Record<string, CommitmentDetail> = {};
      reqs?.forEach((item: CommitmentDetail) => {
        if (!item) return;
        map[item.commitment] = item;
      });
      map[commitment.commitment] = commitment;
      return map;
    },
  });
  if (activeTab === 'tree') return <UtxoTreeFlow familyIds={familyIds} commitment={commitment} chainId={chainId} familyData={query.data} />;

  const parentColumns = [
    {
      title: 'Relation',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => type,
      onCell: () => ({ 'data-label': 'Relation' }),
    },
    {
      title: 'UTXO',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => truncateAddress(id, 12, 8),
      onCell: () => ({ 'data-label': 'UTXO' }),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (_: any, record: any) => {
        const itemData = query.data?.[record.id];
        if (!itemData) return '-';
        return (
          <>
            <AssetAmount amount={itemData.amount} assetId={itemData.asset} chainId={chainId} /> <AssetSymbol assetId={itemData.asset} chainId={chainId} />
          </>
        );
      },
      onCell: () => ({ 'data-label': 'Amount' }),
    },
    {
      title: 'Operate',
      key: 'operate',
      render: (_: any, record: any) => {
        const itemData = query.data?.[record.id];
        if (!itemData) return '-';
        return (
          <OperateButtons>
            <OperateBtn size="small" icon={<EyeOutlined />} onClick={() => navigate(`/utxo/${chainId}/${itemData.commitment}`)} />
          </OperateButtons>
        );
      },
      onCell: () => ({ 'data-label': 'Operate' }),
    },
  ];

  return (
    <FamilyTables>
      <FamilyTableSection>
        <StyledTable>
          <Table
            columns={parentColumns as any}
            onRow={(record) => ({
              onClick: () => {
                navigate(`/utxo/${chainId}/${record.id}`);
              },
              style: { cursor: 'pointer' },
            })}
            dataSource={familyIds.all}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </StyledTable>
      </FamilyTableSection>
    </FamilyTables>
  );
};
